import { PimImportationStatus } from "../../../clients/graphqlTypes.js";

/**
 * UX-facing presentation of an imported catalogue's status. This is a
 * presentation layer over the `PimImportationStatus` enum plus
 * `completionPercentage` — it is NOT a 1:1 mapping. The enum has no `waiting`
 * member; it is derived from the in-progress states before any progress is made.
 */
export type PimImportationStatusKind =
  | "waiting"
  | "indexing"
  | "indexed"
  | "deleted"
  | "error"
  | "hasDuplicate";

export function getPimImportationStatusKind(
  status: PimImportationStatus,
  completionPercentage: number
): PimImportationStatusKind {
  // Both are terminal error states sharing the same chip treatment (red +
  // warning icon, no cancel control), but they keep distinct labels so a
  // duplicate catalogue is not indistinguishable from a hard failure.
  if (status === "failed") return "error";
  if (status === "has_duplicate") return "hasDuplicate";
  if (status === "to_delete" || status === "deleting" || status === "deleted")
    return "deleted";
  if (status === "indexed") return "indexed";
  // Remaining in-progress states (to_index | indexing) are presented as a
  // single "Indexing" phase. The backend exposes separate import vs
  // search-index progress, but the combined `completionPercentage` is what
  // drives the bar; we only split out "Waiting" before any progress is made.
  if (completionPercentage <= 0) return "waiting";
  return "indexing";
}

/** Whether the row is still processing (shows a progress bar + cancel control). */
export function isPimImportationInProgress(
  kind: PimImportationStatusKind
): boolean {
  return kind === "waiting" || kind === "indexing";
}

/** Whether the row is a terminal error state (red chip + warning icon, no cancel). */
export function isPimImportationError(kind: PimImportationStatusKind): boolean {
  return kind === "error" || kind === "hasDuplicate";
}
